// =====================================================================
// SecuriteVerificationLigne.jsx — UNE vérification, faite au téléphone (23-10-2026)
// =====================================================================
// Pensé pour le téléphone et les gants : de gros boutons, une seule chose à
// faire à la fois — « Commencer », la réponse, « Terminé ». Le temps entre les
// deux va tout seul dans les tâches de chaque participant (demande Silviu :
// « ca să evităm propria encodare »). Une vérification faite par plusieurs
// personnes compte le temps à chacune ; on décoche qui n'y était pas.
// Servi par « Mes vérifications » et par la tournée de Vincent (« faire moi-même »).
// =====================================================================

import { useState, useEffect } from 'react';
import { safetyRoutines, errMsg } from '../services/api.js';
import { duree } from '../lib/tournee.js';

const C = {
  line: '#e5e7eb', ink: '#3f4654', mute: '#7b8494', brand: '#714B67',
  ok: '#16a34a', okBg: '#effaf3', crit: '#dc2626', critBg: '#fdf0f0', hs: '#7f1d1d', warnBg: '#fdf5e7',
};
const gros = (bg, fg = '#fff', actif = true) => ({
  width: '100%', minHeight: 64, padding: '0.9rem 1rem', borderRadius: 12, fontSize: '1.15rem', fontWeight: 700,
  border: bg === '#fff' ? `2px solid ${C.line}` : '2px solid transparent', background: actif ? bg : '#f1f5f9',
  color: actif ? fg : '#94a3b8', cursor: actif ? 'pointer' : 'not-allowed',
});
const inp = { width: '100%', padding: '0.75rem', border: `1px solid ${C.line}`, borderRadius: 8, fontSize: '1.05rem', boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 700, color: C.mute, margin: '12px 0 5px', textTransform: 'uppercase', letterSpacing: '.04em' };
const heure = (d) => new Date(d).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });

export default function SecuriteVerificationLigne({ ligne, moi, lang = 'fr', onFini, onRetour, reprise = false }) {
  const [commenceeA, setCommenceeA] = useState(ligne.commenceeA || null);
  const [sansChrono, setSansChrono] = useState(false);
  const [answer, setAnswer] = useState(ligne.faite ? ligne.reponse : '');
  const [comment, setComment] = useState(ligne.commentaire || '');
  const [locationCode, setLocationCode] = useState('');
  const [severity, setSeverity] = useState('');
  // Le temps va à ceux de la vérification ; si on la reprend sans en être, à soi.
  const tous = (ligne.avec || []).map((p) => p.id);
  // Correction d'une vérification faite : on repart de ceux réellement enregistrés, pas de tous.
  const [participants, setParticipants] = useState(() => new Set(ligne.faite && ligne.participants?.length
    ? ligne.participants : (tous.includes(moi) ? tous : [moi])));
  const [maintenant, setMaintenant] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!commenceeA || ligne.faite) return undefined;
    const t = setInterval(() => setMaintenant(Date.now()), 20000);
    return () => clearInterval(t);
  }, [commenceeA, ligne.faite]);

  const titre = (lang === 'ro' && ligne.labelRo) || (lang === 'en' && ligne.labelEn) || ligne.label;
  const nok = answer === 'KO' || answer === 'HS';
  const peutRepondre = Boolean(commenceeA) || sansChrono || ligne.faite;
  const minutes = commenceeA ? Math.max(0, Math.round((maintenant - new Date(commenceeA).getTime()) / 60000)) : null;
  const avecMoi = tous.includes(moi);
  const choixParticipants = (ligne.avec || []).length > 1 || (!avecMoi && (ligne.avec || []).length > 0);

  async function commencer() {
    setBusy(true); setErr(null);
    try { setCommenceeA((await safetyRoutines.commencer(ligne.occurrenceId, ligne.itemId)).commenceeA); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }

  async function terminer() {
    if (!answer) return;
    if (!participants.size) { setErr('Cochez au moins une personne : le temps doit aller à quelqu’un.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await safetyRoutines.terminer(ligne.occurrenceId, ligne.itemId, {
        answer, comment: comment || null, locationCode: locationCode || null,
        severity: severity || undefined, participants: [...participants],
      });
      onFini?.(r);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }

  const basculer = (id) => setParticipants((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, padding: '1rem', marginBottom: '1rem' }}>
      <button type="button" onClick={onRetour}
        style={{ border: 'none', background: 'none', color: C.brand, fontWeight: 700, fontSize: '1rem', padding: '4px 0', cursor: 'pointer' }}>
        ← Retour
      </button>
      <div style={{ fontSize: '1.25rem', fontWeight: 800, color: C.ink, margin: '6px 0 4px' }}>{titre}</div>
      {ligne.helpText && <div style={{ fontSize: '0.9rem', color: C.mute }}>{ligne.helpText}</div>}
      <div style={{ fontSize: '0.9rem', color: C.mute, marginTop: 4 }}>
        {ligne.templateName ? `${ligne.templateName} · ` : ''}{String(ligne.dueDate || '').slice(0, 10)}
        {(ligne.avec || []).length > 0 && <> · avec {(ligne.avec || []).map((p) => p.name).join(', ')}</>}
      </div>
      {reprise && !avecMoi && (
        <div style={{ marginTop: 8, background: C.warnBg, borderRadius: 8, padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: '#92400E' }}>
          Vous reprenez une vérification qui n’est pas la vôtre : le temps vous sera compté.
        </div>
      )}

      {err && <div style={{ marginTop: 10, background: C.critBg, color: '#991B1B', borderRadius: 8, padding: '0.6rem 0.7rem', fontWeight: 600 }}>{err}</div>}

      {/* 1. Commencer — le chronomètre du temps passé */}
      {!ligne.faite && !commenceeA && !sansChrono && (
        <div style={{ marginTop: 14 }}>
          <button type="button" disabled={busy} onClick={commencer} style={gros(C.brand)}>▶ Commencer</button>
          <button type="button" onClick={() => setSansChrono(true)}
            style={{ marginTop: 10, width: '100%', border: 'none', background: 'none', color: C.mute, fontSize: '0.92rem', textDecoration: 'underline', cursor: 'pointer' }}>
            Déjà faite, sans chronomètre (durée habituelle)
          </button>
        </div>
      )}
      {commenceeA && !ligne.faite && (
        <div style={{ marginTop: 12, fontSize: '1rem', color: C.ink, fontWeight: 600 }}>
          ⏱ Commencée à {heure(commenceeA)} — {duree(minutes)}
        </div>
      )}

      {/* 2. La réponse — trois gros boutons, « non applicable » à part */}
      {peutRepondre && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 14 }}>
            {[['OK', C.ok], ['KO', C.crit], ['HS', C.hs]].map(([v, col]) => (
              <button key={v} type="button" onClick={() => setAnswer(v)}
                style={{ ...gros(answer === v ? col : '#fff', answer === v ? '#fff' : col), minHeight: 72, fontSize: '1.3rem' }}
                title={v === 'HS' ? 'Hors service' : v}>
                {v}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setAnswer('NA')}
            style={{ ...gros(answer === 'NA' ? C.mute : '#fff', answer === 'NA' ? '#fff' : C.mute), minHeight: 48, fontSize: '1rem', marginTop: 10 }}>
            Non applicable
          </button>

          {nok && (
            <div style={{ background: C.critBg, borderRadius: 10, padding: '0.2rem 0.8rem 0.8rem', marginTop: 12 }}>
              <label style={lbl}>Ce qu’on voit</label>
              <textarea style={{ ...inp, minHeight: 70 }} value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder="Précisez : ça part dans l’action corrective" />
              <label style={lbl}>Emplacement (scannez l’étiquette ou tapez)</label>
              <input style={{ ...inp, fontFamily: 'Consolas, monospace' }} value={locationCode} onChange={(e) => setLocationCode(e.target.value)} />
              <label style={lbl}>Gravité</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {[['NORMALE', 'Normale', '30 jours'], ['HAUTE', 'Haute', '7 jours'], ['CRITIQUE', 'Critique', 'demain']].map(([v, l, d]) => (
                  <button key={v} type="button" onClick={() => setSeverity(severity === v ? '' : v)}
                    style={{ ...gros(severity === v ? C.crit : '#fff', severity === v ? '#fff' : C.ink), minHeight: 56, fontSize: '0.95rem', padding: '0.4rem' }}>
                    {l}<div style={{ fontSize: '0.75rem', fontWeight: 600 }}>{d}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {!nok && answer && (
            <>
              <label style={lbl}>Remarque (facultatif)</label>
              <input style={inp} value={comment} onChange={(e) => setComment(e.target.value)} />
            </>
          )}

          {/* 3. À qui compter le temps */}
          {choixParticipants && (
            <>
              <label style={lbl}>Le temps va à</label>
              <div style={{ display: 'grid', gap: 8 }}>
                {[...(ligne.avec || []), ...(avecMoi ? [] : [{ id: moi, name: 'Moi' }])].map((p) => (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: '1.05rem', minHeight: 44, cursor: 'pointer' }}>
                    <input type="checkbox" checked={participants.has(p.id)} onChange={() => basculer(p.id)}
                      style={{ width: 26, height: 26 }} />
                    {p.name}
                  </label>
                ))}
              </div>
            </>
          )}

          <button type="button" disabled={busy || !answer} onClick={terminer} style={{ ...gros(C.ok, '#fff', Boolean(answer) && !busy), marginTop: 16 }}>
            {busy ? 'Enregistrement…' : (ligne.faite ? '✓ Corriger' : '✓ Terminé')}
          </button>
        </>
      )}
    </div>
  );
}
